import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles, Loader2, CheckCircle2, XCircle, UserPlus, Link2, AlertTriangle,
  Image as ImageIcon, Mic, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDate, formatDateTime } from "@/lib/format";
import { PRIORIDADES } from "@/lib/wo";
import { StatusChip } from "@/components/common/StatusChip";
import { EmptyState } from "@/components/common/EmptyState";
import { IntakeConfidenceField, HighlightedText } from "@/features/intake/IntakeConfidenceField";
import { ServicePicker } from "@/components/common/ServicePicker";
import { PropertyPicker } from "@/components/common/PropertyPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const CONFIDENCE_TONE = (c) => (c == null ? "gray" : c >= 0.75 ? "green" : c >= 0.4 ? "amber" : "red");

const PedidoCard = ({ message, pedido, pedidoIndex, borrador, duplicadoInfo, properties, services,
                     hovered, setHovered, onDone, onDiscard, modoPiloto = false,
                     propertiesLoaded = true }) => {   // Fix P
  const { t } = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const [propertyId, setPropertyId] = useState(borrador?.property_id || pedido.edificio_id_final || "");
  // Fix V (2026-10-19): si la unidad no está cargada, se propone el número que vino en el mensaje.
  const [unitText, setUnitText] = useState(borrador?.unit_etiqueta || pedido.unidad_sugerida || "");
  const [unitId, setUnitId] = useState(borrador?.unit_id || pedido.unidad_id_final || "");
  const [serviceId, setServiceId] = useState(borrador?.service_type_id || pedido.servicio_id_final || "");
  const [descripcion, setDescripcion] = useState(borrador?.descripcion || pedido.descripcion_final || "");
  const [prioridad, setPrioridad] = useState(borrador?.prioridad || pedido.prioridad_final || "media");
  // Fix V (2026-10-19): día del trabajo (sale del mensaje; si es move-out, el día siguiente).
  const [fecha, setFecha] = useState(borrador?.fecha_programada || pedido.fecha_programada_final || "");
  const [guardarContacto, setGuardarContacto] = useState(true);

  const { data: units } = useQuery({
    queryKey: ["units-of", propertyId],
    enabled: !!propertyId,
    queryFn: async () => (await api.get("/units", { params: { property_id: propertyId, page_size: 500 } })).data,
  });

  const property = (properties || []).find((p) => p.id === propertyId);
  const service = (services || []).find((s) => s.id === serviceId);
  const unitSuggestions = (units?.items || []).filter(
    (u) => !unitText || u.etiqueta.toLowerCase().includes(unitText.toLowerCase())
  ).slice(0, 6);

  const confirmado = !!borrador?.confirmado_ia;
  const descartado = borrador?.status === "cancelada";
  const esBorrador = !!borrador && !confirmado && !descartado;
  const sinCrear = !borrador;

  const buildPayload = () => ({
    property_id: propertyId || undefined,
    unit_id: unitId || undefined,
    unit_nueva: !unitId && unitText ? unitText : undefined,
    service_type_id: serviceId || undefined,
    descripcion: descripcion || undefined,
    prioridad,
    fecha_programada: fecha || "",
    guardar_contacto: guardarContacto,
  });

  const confirmar = async (thenAssign) => {
    if (!propertyId || !serviceId) { toast.error(t("app.required")); return; }
    setBusy(true);
    try {
      await api.post(`/intake/work-orders/${borrador.id}/confirm`, buildPayload());
      toast.success(t("intake.review.confirmed"));
      onDone();
      if (thenAssign) navigate(`/ordenes/${borrador.id}?assign=1`);
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  const crearOrden = async () => {
    if (!propertyId || !serviceId || !descripcion.trim()) { toast.error(t("app.required")); return; }
    setBusy(true);
    try {
      await api.post(`/intake/messages/${message.id}/create-order`, { ...buildPayload(), pedido_index: pedidoIndex });
      toast.success(t("intake.review.created"));
      onDone();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  const vincularAdicional = async () => {
    setBusy(true);
    try {
      await api.post(`/intake/messages/${message.id}/link-additional`, {
        pedido_index: pedidoIndex, target_wo_id: duplicadoInfo.id,
      });
      toast.success(t("intake.review.linked"));
      onDone();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  const hoverProps = (fragment) => ({
    onHover: () => fragment && setHovered(fragment),
    onLeave: () => setHovered(null),
  });

  return (
    <div
      data-testid={`intake-pedido-card-${pedidoIndex}`}
      className="overflow-hidden rounded-[10px] border border-line bg-white"
    >
      <div className="flex items-center justify-between border-b border-line bg-surface-sunken px-3 py-2">
        <span className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink">
          <Sparkles className="h-3.5 w-3.5 text-brand" /> {t("intake.review.pedido")} {pedidoIndex + 1}
        </span>
        {confirmado && <StatusChip tone="green" testId={`intake-confirmed-${pedidoIndex}`} label={`${t("intake.status.confirmado")} · ${borrador.code}`} />}
        {descartado && <StatusChip tone="gray" label={t("intake.status.descartado")} />}
        {esBorrador && <StatusChip tone="amber" label={borrador.code} />}
      </div>

      {/* Fix N (2026-09-28): en modo piloto, al cliente de este edificio no le sale nada automático. */}
      {modoPiloto && property && !property.piloto && (
        <div data-testid={`intake-outside-pilot-${pedidoIndex}`}
          className="flex items-center gap-2 border-b border-line bg-surface-sunken px-3 py-2 text-[12px] text-ink-secondary">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-state-warn" />
          {t("intake.review.outside_pilot")}
        </div>
      )}

      {duplicadoInfo && esBorrador && (
        <div className="flex flex-wrap items-center gap-2 border-b border-state-warn-soft bg-state-warn-soft px-3 py-2 text-[12px] text-state-warn">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {t("intake.review.duplicate_warning", { code: duplicadoInfo.code })}
          <button
            data-testid={`intake-view-duplicate-${pedidoIndex}`}
            onClick={() => navigate(`/ordenes/${duplicadoInfo.id}`)}
            className="ml-auto inline-flex items-center gap-1 font-medium underline"
          >
            {t("intake.review.view_order")} <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="grid gap-3 p-3 sm:grid-cols-2">
        <div className="space-y-0.5 rounded-[8px] border border-line p-2">
          <p className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">
            {t("intake.review.ai_block")}
          </p>
          <IntakeConfidenceField testId={`intake-field-edificio-${pedidoIndex}`}
            label={t("intake.review.field_edificio")} value={property?.nombre}
            confidence={pedido.edificio_confianza_final} fragment={pedido.edificio_fragmento} {...hoverProps(pedido.edificio_fragmento)} />
          <IntakeConfidenceField testId={`intake-field-unidad-${pedidoIndex}`}
            label={t("intake.review.field_unidad")} value={unitText || "—"}
            confidence={pedido.unidad_confianza} fragment={pedido.unidad_fragmento} {...hoverProps(pedido.unidad_fragmento)} />
          <IntakeConfidenceField testId={`intake-field-servicio-${pedidoIndex}`}
            label={t("intake.review.field_servicio")} value={service?.nombre}
            confidence={pedido.servicio_confianza} fragment={pedido.servicio_fragmento} {...hoverProps(pedido.servicio_fragmento)} />
          <IntakeConfidenceField testId={`intake-field-descripcion-${pedidoIndex}`}
            label={t("intake.review.field_descripcion")} value={pedido.descripcion_final}
            confidence={pedido.descripcion_confianza} fragment={pedido.descripcion_fragmento} {...hoverProps(pedido.descripcion_fragmento)} />
          <IntakeConfidenceField testId={`intake-field-prioridad-${pedidoIndex}`}
            label={t("intake.review.field_prioridad")} value={t(`priority.${pedido.prioridad_final}`)}
            confidence={pedido.prioridad_confianza} fragment={pedido.prioridad_fragmento} {...hoverProps(pedido.prioridad_fragmento)} />
          <IntakeConfidenceField testId={`intake-field-fecha-${pedidoIndex}`}
            label={t("intake.review.field_fecha")}
            value={pedido.due_at_final ? formatDateTime(pedido.due_at_final) : (pedido.fecha_limite_texto || "—")}
            confidence={pedido.fecha_limite_texto ? 0.6 : null} fragment={pedido.fecha_limite_texto} {...hoverProps(pedido.fecha_limite_texto)} />
          <IntakeConfidenceField testId={`intake-field-fecha-trabajo-${pedidoIndex}`}
            label={t("intake.review.field_fecha_trabajo")}
            value={pedido.fecha_programada_final
              ? formatDate(pedido.fecha_programada_final)
                + (String(pedido.fecha_es_move_out) === "true" ? ` · ${t("intake.review.move_out_note")}` : "")
              : "—"}
            confidence={pedido.fecha_trabajo_fragmento ? 0.8 : null} fragment={pedido.fecha_trabajo_fragmento}
            {...hoverProps(pedido.fecha_trabajo_fragmento)} />
        </div>

        <div className="space-y-2 rounded-[8px] border border-line p-2">
          <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">
            {t("intake.review.form_block")}
          </p>
          <div>
            <Label className="text-[11.5px]">{t("intake.review.field_edificio")}</Label>
            {/* Fix K (2026-09-28): era una lista plana de 141 edificios sin buscador. */}
            <PropertyPicker
              small
              cargando={!propertiesLoaded}
              properties={properties}
              value={propertyId}
              disabled={confirmado || descartado}
              testId={`intake-form-property-${pedidoIndex}`}
              onChange={(v) => { setPropertyId(v); setUnitId(""); setUnitText(""); }}
            />
          </div>
          <div className="relative">
            <Label className="text-[11.5px]">{t("intake.review.field_unidad")}</Label>
            <Input
              data-testid={`intake-form-unit-${pedidoIndex}`}
              value={unitText} disabled={confirmado || descartado || !propertyId}
              onChange={(e) => { setUnitText(e.target.value); setUnitId(""); }}
              placeholder={t("wo.form.unit_ph")}
              className="mt-0.5 h-8 rounded-[6px] text-[12.5px]"
            />
            {!!unitText && !unitId && !!unitSuggestions.length && !confirmado && !descartado && (
              <ul className="absolute z-30 mt-1 w-full overflow-hidden rounded-[8px] border border-line bg-white shadow-lg">
                {unitSuggestions.map((u) => (
                  <li key={u.id}>
                    <button type="button" data-testid={`intake-form-unit-option-${pedidoIndex}-${u.etiqueta}`}
                      onClick={() => { setUnitId(u.id); setUnitText(u.etiqueta); }}
                      className="block w-full px-2.5 py-1.5 text-left text-[12.5px] hover:bg-line">
                      {u.etiqueta}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <Label className="text-[11.5px]">{t("intake.review.field_servicio")}</Label>
            {/* Fix K (2026-09-28): era una lista plana de más de 100 servicios sin buscador. */}
            <ServicePicker
              small
              services={services}
              value={serviceId}
              disabled={confirmado || descartado}
              testId={`intake-form-service-${pedidoIndex}`}
              onChange={setServiceId}
            />
          </div>
          <div>
            <Label className="text-[11.5px]">{t("intake.review.field_prioridad")}</Label>
            <Select value={prioridad} onValueChange={setPrioridad} disabled={confirmado || descartado}>
              <SelectTrigger data-testid={`intake-form-priority-${pedidoIndex}`} className="mt-0.5 h-8 rounded-[6px] text-[12.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORIDADES.map((p) => <SelectItem key={p} value={p}>{t(`priority.${p}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11.5px]">{t("intake.review.field_fecha_trabajo")}</Label>
            <Input type="date" data-testid={`intake-form-date-${pedidoIndex}`}
              value={fecha} disabled={confirmado || descartado}
              onChange={(e) => setFecha(e.target.value)}
              className="mt-0.5 h-8 rounded-[6px] text-[12.5px]" />
          </div>
          <div>
            <Label className="text-[11.5px]">{t("intake.review.field_descripcion")}</Label>
            <Textarea
              data-testid={`intake-form-description-${pedidoIndex}`}
              value={descripcion} disabled={confirmado || descartado}
              onChange={(e) => setDescripcion(e.target.value)} rows={2}
              className="mt-0.5 rounded-[6px] text-[12.5px]"
            />
          </div>
          {!confirmado && !descartado && (
            <label className="flex items-center gap-2 pt-0.5 text-[11.5px] text-ink-secondary">
              <Checkbox data-testid={`intake-form-save-contact-${pedidoIndex}`} checked={guardarContacto} onCheckedChange={setGuardarContacto} />
              {t("intake.review.save_contact")}
            </label>
          )}
        </div>
      </div>

      {(esBorrador || sinCrear) && (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-line bg-surface-sunken px-3 py-2">
          {sinCrear ? (
            <Button data-testid={`intake-create-order-${pedidoIndex}`} disabled={busy} onClick={crearOrden}
              className="h-8 rounded-[6px] bg-brand text-[12.5px] hover:bg-brand-hover">
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
              {t("intake.review.create_order")}
            </Button>
          ) : (
            <>
              <Button data-testid={`intake-confirm-${pedidoIndex}`} disabled={busy} onClick={() => confirmar(false)}
                className="h-8 rounded-[6px] bg-brand text-[12.5px] hover:bg-brand-hover">
                {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />}
                {t("intake.review.confirm")}
              </Button>
              <Button variant="outline" data-testid={`intake-confirm-assign-${pedidoIndex}`} disabled={busy} onClick={() => confirmar(true)}
                className="h-8 rounded-[6px] text-[12.5px]">
                <UserPlus className="mr-1.5 h-3.5 w-3.5" /> {t("intake.review.confirm_assign")}
              </Button>
              {duplicadoInfo && (
                <Button variant="outline" data-testid={`intake-link-additional-${pedidoIndex}`} disabled={busy} onClick={vincularAdicional}
                  className="h-8 rounded-[6px] border-state-warn-soft text-[12.5px] text-state-warn">
                  <Link2 className="mr-1.5 h-3.5 w-3.5" /> {t("intake.review.link_additional")}
                </Button>
              )}
              <Button variant="outline" data-testid={`intake-discard-${pedidoIndex}`} disabled={busy}
                onClick={() => onDiscard(borrador)}
                className="ml-auto h-8 rounded-[6px] border-state-critical-soft text-[12.5px] text-state-critical">
                <XCircle className="mr-1.5 h-3.5 w-3.5" /> {t("intake.review.discard")}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const IntakeReviewPanel = ({ messageId, onChanged }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();   // Fix O
  const [hovered, setHovered] = useState(null);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [processing, setProcessing] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["intake-message", messageId],
    enabled: !!messageId,
    queryFn: async () => (await api.get(`/intake/messages/${messageId}`)).data,
  });

  const { data: propertiesData } = useQuery({
    queryKey: ["properties-options-all"],
    queryFn: async () => (await api.get("/properties", { params: { page_size: 200 } })).data,
  });
  const { data: servicesData } = useQuery({
    queryKey: ["service-types"],
    queryFn: async () => (await api.get("/service-types")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["intake-message", messageId] });
    qc.invalidateQueries({ queryKey: ["intake-messages"] });
    onChanged?.();
  };

  const processNow = async () => {
    setProcessing(true);
    try {
      await api.post(`/intake/messages/${messageId}/process`);
      toast.success(t("intake.review.processed"));
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setProcessing(false);
    }
  };

  const confirmDiscard = async () => {
    setDiscarding(true);
    try {
      await api.post(`/work-orders/${discardTarget.id}/transition`, { destino: "cancelada", motivo });
      toast.success(t("intake.review.discarded"));
      setDiscardTarget(null);
      setMotivo("");
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setDiscarding(false);
    }
  };

  if (!messageId) {
    return (
      <div className="flex flex-1 items-center justify-center bg-surface-sunken">
        <EmptyState icon={Sparkles} testId="intake-review-empty"
          title={t("intake.review.select_title")} description={t("intake.review.select_desc")} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex-1 space-y-3 bg-surface-sunken p-5">
        <Skeleton className="h-[100px] rounded-[10px]" />
        <Skeleton className="h-[220px] rounded-[10px]" />
      </div>
    );
  }

  const { mensaje, borradores, duplicados, contacto } = data;
  const pedidos = mensaje.ai_extraction?.pedidos || [];
  const properties = propertiesData?.items || [];
  const services = servicesData?.items || [];

  return (
    <div className="flex-1 overflow-y-auto bg-surface-sunken p-5" data-testid="intake-review-panel">
      <div className="mb-3 overflow-hidden rounded-[10px] border border-line bg-white">
        <div className="flex items-center justify-between border-b border-line px-3 py-2">
          <div className="flex items-center gap-2 text-[12px] text-ink-tertiary">
            <StatusChip tone="blue" label={t(`intake.channel.${mensaje.canal}`)} />
            <span className="font-medium text-ink">
              {mensaje.remitente_nombre || mensaje.remitente_contacto || t("intake.unknown_sender")}
            </span>
            {contacto && <StatusChip tone="green" label={t("intake.review.known_contact")} />}
            {mensaje.reenviado_por && (
              <span data-testid="intake-forwarded-by">
                · {t("intake.review.forwarded_by", { quien: mensaje.reenviado_por.nombre || mensaje.reenviado_por.contacto || "—" })}
              </span>
            )}
            <span>· {formatDateTime(mensaje.created_at)}</span>
          </div>
          {["sin_procesar", "respuesta_sub"].includes(mensaje.estado_intake) && (
            <Button data-testid="intake-process-now" disabled={processing} onClick={processNow}
              className="h-8 rounded-[6px] bg-ink text-[12.5px] hover:bg-ink">
              {processing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              {mensaje.estado_intake === "respuesta_sub"
                ? t("intake.review.process_as_order")
                : t("intake.review.process_now")}
            </Button>
          )}
        </div>
        <div className="p-3">
          <p data-testid="intake-original-body" className="whitespace-pre-wrap text-[13px] leading-relaxed text-ink">
            <HighlightedText text={mensaje.cuerpo} fragment={hovered} />
          </p>
          {mensaje.audio_url && (
            <div className="mt-2 flex items-center gap-2">
              <Mic className="h-3.5 w-3.5 text-ink-tertiary" />
              <audio data-testid="intake-audio-player" controls src={mensaje.audio_url} className="h-8" />
            </div>
          )}
          {!!mensaje.adjuntos?.length && (
            <div className="mt-2 flex flex-wrap gap-2">
              {mensaje.adjuntos.map((a, i) => (
                <a key={i} href={a.url} target="_blank" rel="noreferrer" data-testid={`intake-attachment-${i}`}
                  className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-[6px] border border-line bg-surface-sunken">
                  {a.content_type?.startsWith("image/")
                    ? <img src={a.url} alt="" className="h-full w-full object-cover" />
                    : <ImageIcon className="h-5 w-5 text-ink-tertiary" />}
                </a>
              ))}
            </div>
          )}
          {!!mensaje.partes?.length && (
            <div className="mt-2 space-y-1.5 border-t border-line pt-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">
                {t("intake.review.grouped_parts")}
              </p>
              {mensaje.partes.map((p, i) => (
                <p key={i} data-testid={`intake-parte-${i}`} className="text-[12.5px] text-ink-secondary">{p.cuerpo}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      {mensaje.estado_intake === "no_accionable" && (
        <EmptyState icon={XCircle} testId="intake-no-accionable" title={t("intake.review.no_accionable_title")}
          description={t("intake.review.no_accionable_desc")} />
      )}
      {mensaje.estado_intake === "agrupado" && (
        <EmptyState icon={Link2} testId="intake-grouped" title={t("intake.review.grouped_title")}
          description={t("intake.review.grouped_desc")} />
      )}
      {mensaje.estado_intake === "sin_procesar" && (
        <EmptyState icon={Sparkles} testId="intake-not-processed" title={t("intake.review.not_processed_title")}
          description={t("intake.review.not_processed_desc")} />
      )}
      {/* Fix O (2026-09-29): lo que escribió un contratista no es un pedido nuevo. */}
      {mensaje.estado_intake === "respuesta_sub" && (
        <div data-testid="intake-sub-reply" className="rounded-[10px] border border-line bg-white p-3">
          <p className="text-[13px] font-semibold text-ink">{t("intake.review.sub_reply_title")}</p>
          <p className="mt-0.5 text-[12.5px] text-ink-secondary">
            {mensaje.wo_code
              ? t("intake.review.sub_reply_desc", { code: mensaje.wo_code })
              : t("intake.review.sub_reply_no_order")}
          </p>
          {mensaje.respuesta_wo_id && (
            <Button variant="outline" data-testid="intake-sub-reply-open"
              onClick={() => navigate(`/ordenes/${mensaje.respuesta_wo_id}`)}
              className="mt-2 h-8 rounded-[6px] text-[12.5px]">
              {t("intake.review.view_order")} <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          )}
        </div>
      )}

      <div className="space-y-3">
        {pedidos.map((pedido, i) => {
          const borrador = pedido.wo_id ? borradores.find((b) => b.id === pedido.wo_id) : null;
          const duplicadoInfo = borrador ? duplicados[borrador.id] : null;
          return (
            <PedidoCard
              key={i} message={mensaje} pedido={pedido} pedidoIndex={i} borrador={borrador}
              duplicadoInfo={duplicadoInfo} properties={properties} services={services}
              propertiesLoaded={!!propertiesData}
              modoPiloto={!!data.modo_piloto}
              hovered={hovered} setHovered={setHovered} onDone={refresh}
              onDiscard={setDiscardTarget}
            />
          );
        })}
      </div>

      <Dialog open={!!discardTarget} onOpenChange={(v) => !v && setDiscardTarget(null)}>
        <DialogContent data-testid="intake-discard-dialog" className="max-w-[440px] rounded-[10px]">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{t("intake.review.discard_title")}</DialogTitle>
          </DialogHeader>
          <Textarea data-testid="intake-discard-motivo" value={motivo} rows={3}
            onChange={(e) => setMotivo(e.target.value)} placeholder={t("intake.review.discard_ph")}
            className="rounded-[6px] text-[13px]" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscardTarget(null)} className="h-9 rounded-[6px] text-[13px]">
              {t("app.cancel")}
            </Button>
            <Button data-testid="intake-discard-confirm" disabled={motivo.trim().length < 5 || discarding}
              onClick={confirmDiscard} className="h-9 rounded-[6px] bg-state-critical text-[13px] hover:bg-state-critical">
              {discarding && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t("intake.review.discard")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
